import {
  createClient,
  type Client,
} from '@data-access/geospatial/fx-workspace-service';

import { ApiFxWorkspaceRepository } from './api-fx-workspace-repository';
import type { FxBreakline } from './fx-workspace-breakline.types';
import type { FxWorkspace } from './fx-workspace.types';
import type { FxWorkspaceRepository } from './fx-workspace-repository';

const unavailableFxWorkspaceRepository: FxWorkspaceRepository = {
  async list(): Promise<readonly FxWorkspace[]> {
    return [];
  },
  async create(): Promise<FxWorkspace> {
    throw new Error('fxWorkspaceDomain is not configured');
  },
  async delete(): Promise<void> {
    throw new Error('fxWorkspaceDomain is not configured');
  },
  async listReferences(): Promise<readonly string[]> {
    return [];
  },
  async addReference(): Promise<void> {
    throw new Error('fxWorkspaceDomain is not configured');
  },
  async syncReferences(): Promise<void> {
    throw new Error('fxWorkspaceDomain is not configured');
  },
  async removeReference(): Promise<void> {
    throw new Error('fxWorkspaceDomain is not configured');
  },
  async listBreaklines(): Promise<readonly FxBreakline[]> {
    return [];
  },
  async createBreakline(): Promise<FxBreakline> {
    throw new Error('fxWorkspaceDomain is not configured');
  },
};

export interface CreateFxWorkspaceRepositoryConfig {
  fxWorkspaceDomain?: string;
  clientName: string;
  clientVersion: string;
  getAccessToken(): Promise<string>;
  getTenantTrn(): string | undefined;
  onResponseError(response: Response, request: Request): Promise<Response>;
}

export function createFxWorkspaceRepository(
  config: CreateFxWorkspaceRepositoryConfig
): FxWorkspaceRepository {
  const domain = config.fxWorkspaceDomain?.trim();
  if (domain === undefined || domain === '') {
    return unavailableFxWorkspaceRepository;
  }

  const client = createFxWorkspaceClient({
    baseUrl: domain,
    getAccessToken: config.getAccessToken,
    onResponseError: config.onResponseError,
  });

  return new ApiFxWorkspaceRepository({
    client,
    clientName: config.clientName,
    clientVersion: config.clientVersion,
    getTenantTrn: config.getTenantTrn,
  });
}

interface CreateFxWorkspaceClientConfig {
  baseUrl: string;
  getAccessToken(): Promise<string>;
  onResponseError?(response: Response, request: Request): Promise<Response>;
}

function createFxWorkspaceClient(
  config: CreateFxWorkspaceClientConfig
): Client {
  const client = createClient({ baseUrl: config.baseUrl });

  client.interceptors.request.use(async (requestConfig: Request) => {
    const token = await config.getAccessToken();
    requestConfig.headers.set('Authorization', `Bearer ${token}`);
    return requestConfig;
  });

  if (config.onResponseError) {
    client.interceptors.response.use(config.onResponseError);
  }

  return client;
}
